import { useActionConfirmation } from '../components/ActionFeedbackProvider';
// React & Hooks
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Icons
import { Home, Plus, Pencil, ChevronLeft, ChevronRight, Trash, RotateCcw, ShoppingCart } from "lucide-react";

// API client
import api from "../lib/api";

// Modals
import AddSaleModal from "../components/modals/AddSaleModal";
import ReturnSaleModal from "../components/modals/ReturnSaleModal";
import EditSaleModal from "../components/modals/EditSaleModal";
import PageHeading from "../components/PageHeading";

// i18n
import { useTranslation } from "react-i18next";

const SalesScreen = () => {
  // States
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    return today;
  });

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();

  const confirmAction = useActionConfirmation();

  // Base button style with Tailwind dark: classes
  const baseBtn =
    "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold shadow transition " +
    "bg-black text-white hover:bg-white hover:text-black " +
    "dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white";

  // Fetch sales when selectedDate changes
  useEffect(() => {
    fetchSales();
  }, [selectedDate]);

  // Fetch sales from API
  const fetchSales = async () => {
    setLoading(true);
    try {
      const res = await api.get("/sales", { params: { date: selectedDate } });
      setSales(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete sale
  const handleDelete = async (id, productName) => {
    const confirmDelete = await confirmAction(`${t("confirmDelete")} "${productName}"?`);
    if (!confirmDelete) return;

    try {
      await api.delete(`/sales/${id}`);
      fetchSales();
    } catch (err) {
      console.error(err);
    }
  };

  // Calculate totals
  const summary = () => {
    let totalSent = sales.reduce((sum, s) => sum + s.sentWeight, 0);
    let totalReturned = sales.reduce((sum, s) => sum + (s.returnWeight || 0), 0);
    let totalSold = totalSent - totalReturned;
    return { totalSent, totalReturned, totalSold };
  };

  const totals = summary();

  // Shift calendar date
  const shiftDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split("T")[0]);
  };

  // Get weekday name from i18n
  const getDayName = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay(); // 0 = Sunday ... 6 = Saturday
    return t(`days.${dayIndex}`);
  };

  // Render screen
  return (
    <div className="min-h-screen p-6">
      {/* Home button */}
      <div className="flex justify-center mb-6">
        <button className={baseBtn} onClick={() => navigate("/")}>
          <Home size={18} />
          {t("home")}
        </button>
      </div>

      <PageHeading icon={<ShoppingCart size={26} />} title={t("sales")} />

      {/* Calendar bar */}
      <div className="flex items-center justify-center gap-4 mb-4 w-full">
        <button
          className={`${baseBtn} !px-2 !py-2 rounded-full`}
          onClick={() => shiftDate(-1)}
        >
          <ChevronLeft size={20} />
        </button>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm shadow w-48 text-center
                     bg-white text-black
                     dark:bg-gray-900 dark:text-white dark:border-gray-600
                     [color-scheme:light] dark:[color-scheme:dark]"
        />
        <button
          className={`${baseBtn} !px-2 !py-2 rounded-full`}
          onClick={() => shiftDate(1)}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Day name */}
      <h1 className="text-3xl font-bold text-center mb-6">
        {getDayName(selectedDate)}
      </h1>

      {/* Add Sale button */}
      <div className="flex justify-center mb-6">
        <button className={baseBtn} onClick={() => setShowSaleModal(true)}>
          <Plus size={18} />
          {t("addSale")}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="p-4 rounded-xl shadow bg-white/80 dark:bg-gray-800/80">
          <h3 className="text-lg font-bold">{t("sent")}</h3>
          <p className="text-xl">{totals.totalSent.toFixed(2)} KG</p>
        </div>
        <div className="p-4 rounded-xl shadow bg-white/80 dark:bg-gray-800/80">
          <h3 className="text-lg font-bold">{t("returned")}</h3>
          <p className="text-xl">{totals.totalReturned.toFixed(2)} KG</p>
        </div>
        <div className="p-4 rounded-xl shadow bg-white/80 dark:bg-gray-800/80">
          <h3 className="text-lg font-bold">{t("sold")}</h3>
          <p className="text-xl">{totals.totalSold.toFixed(2)} KG</p>
        </div>
      </div>

      {/* Sales cards */}
      {loading ? (
        <p>{t("loading")}</p>
      ) : sales.length === 0 ? (
        <p className="text-center">{t("noSales")}</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {sales.map((s) => (
            <div
              key={s.id}
              className="px-3 py-2 rounded-lg shadow bg-white/80 dark:bg-gray-800/80 flex flex-col justify-between"
            >
              {/* Sale details */}
              <div>
                <p className="font-semibold text-sm">{s.productName}</p>
                <p className="text-xs">{t("sent")}: {s.sentWeight.toFixed(2)} KG</p>
                <p className="text-xs">{t("returned")}: {(s.returnWeight || 0).toFixed(2)} KG</p>
                <p className="text-xs">{t("sold")}: {(s.sentWeight - (s.returnWeight || 0)).toFixed(2)} KG</p>

                {/* Barrels sent */}
                {s.barrels && s.barrels.length > 0 && (
                  <div className="mt-2 text-xs">
                    <p className="font-semibold">{t("barrelsSent")}:</p>
                    <ul className="list-disc list-inside">
                      {s.barrels.map((b, i) => (
                        <li key={i}>
                          {t("barrel")} {i + 1}: {b.toFixed(2)} KG
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Barrels returned */}
                {s.returnBarrels && s.returnBarrels.length > 0 && (
                  <div className="mt-2 text-xs">
                    <p className="font-semibold">{t("barrelsReturned")}:</p>
                    <ul className="list-disc list-inside">
                      {s.returnBarrels.map((rb, i) => (
                        <li key={i}>
                          {t("return")} {i + 1}: {rb.toFixed(2)} KG
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-1 mt-2">
                <button
                  className={baseBtn}
                  onClick={() => {
                    setSelectedSale(s);
                    setShowReturnModal(true);
                  }}
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  className={baseBtn}
                  onClick={() => {
                    setSelectedSale(s);
                    setShowEditModal(true);
                  }}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className={baseBtn}
                  onClick={() => handleDelete(s.id, s.productName)}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showSaleModal && (
        <AddSaleModal
          isOpen={showSaleModal}
          onClose={() => setShowSaleModal(false)}
          onSaved={fetchSales}
          selectedDate={selectedDate}
        />
      )}

      {showReturnModal && selectedSale && (
        <ReturnSaleModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          onSaved={fetchSales}
          sale={selectedSale}
        />
      )}

      {showEditModal && selectedSale && (
        <EditSaleModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSaved={fetchSales}
          sale={selectedSale}
        />
      )}
    </div>
  );
};

export default SalesScreen;
